// BEGIN /src/components/Channels/ConversationsList.tsx
import { useAppDispatch, useAppSelector } from "src/store";
import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, CircularProgress, Hidden, IconButton, Skeleton, Typography,
  Tooltip, Dialog, DialogTitle, DialogContent,
  DialogContentText, DialogActions, InputBase,
  TableRow, TableCell, Table, TableBody
} from "@mui/material";
import { Button } from "@mui/material";
import { Send, AutoAwesome, PlayArrow, Pause, Delete, FilterListOff } from '@mui/icons-material';
import { Search as SearchIcon, Clear as ClearIcon } from '@mui/icons-material';
import ConversationCreateDialogV2 from "./ConversationCreateDialogV2";
import emptyConversationsListSvg from "src/assets/svg/Oppy_careers_Inspector-Appraiser.svg";
import { fetchConversationsWithMessages, selectConversationsWithContacts, updateConversationDeliveryState, deleteConversations, selectLimit, selectHasMore, selectLastCursor, fetchConversationsFullData } from "src/features/account/conversations.slice";
import { AddComment } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { selectAllFilteredConversations, selectIsFilterActive, fetchFilteredConversations, setFilter, selectPriorityConversations, selectHasMorePriority, fetchPriorityConversations, selectFilteredLastCursor } from "src/features/account/filteredConversations.slice";
import ConversationsTable from './ConversationsTable';
import { deliverMessageNow } from "src/features/account/messages.slice";
import { conversationAutocompleteNow } from "src/features/account/conversations.slice";
import { useSnackbar } from 'notistack';
import GenerateRepliesConfirmationDialog from './GenerateRepliesConfirmationDialog';
import { httpClient } from 'src/libs';
import ConversationFilters from './ConversationFilters';
import { filterConversations } from '../../utils/conversationUtils';
import { alpha } from '@mui/material/styles';

interface ConversationsListProps {
  conversationsListStyle?: 'open' | 'closed';
  handleConversationsListToggleClick: () => void;
}

const getStyles = (darkMode: boolean, theme: any) => ({
  mainContainer: {
    display: 'flex',
    width: '100%',
    flexDirection: 'column',
    flexGrow: 1,
    borderRadius: { xs: 0, sm: 3 },
    height: '100%',
    maxHeight: '100%',
    overflow: 'hidden',
    minWidth: '100%',
    maxWidth: '100vw',
    position: 'relative',
    bgcolor: darkMode ? 'transparent' : 'background.default',
    color: darkMode ? 'grey.300' : 'text.primary',
    // No padding - let content go all the way up
    paddingTop: 0,
    paddingBottom: 0,
  },
  topBar: {
    position: 'sticky',
    top: 0,  // Start right at the top
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    p: 1.5,
    // Normal padding - safe area is handled at container level
    pt: 1.5,
    background: darkMode 
      ? `linear-gradient(180deg, ${alpha(theme.palette.background.glass, 0.98)} 0%, ${alpha(theme.palette.background.glass, 0.95)} 100%)`
      : `linear-gradient(180deg, ${alpha(theme.palette.grey[50], 0.98)} 0%, ${alpha(theme.palette.grey[50], 0.95)} 100%)`,
    backdropFilter: 'blur(10px)',
    width: '100%',
    borderBottom: 1,
    borderColor: darkMode ? 'grey.700' : 'grey.200',
    zIndex: 10,
    // Add subtle shadow for depth on mobile
    boxShadow: {
      xs: darkMode ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.08)',
      sm: 'none'
    },
    '& .MuiTypography-root': {
      color: darkMode ? 'grey.100' : 'text.primary',
    },
    // Smooth transition for collapsing/expanding
    transition: 'all 0.3s ease',
  },
  topBarTitleAndFilterContent: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 2
  },
  topBarAction: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 1,
    '& .MuiIconButton-root': {
      color: darkMode ? 'grey.300' : 'text.secondary',
      '&:hover': {
        color: 'primary.main',
        bgcolor: theme.palette.background.overlay
      }
    },
    '& .MuiButton-root': {
      color: darkMode ? 'grey.300' : 'text.primary',
      borderColor: darkMode ? 'grey.700' : 'grey.300',
      '&:hover': {
        borderColor: 'primary.main',
        bgcolor: theme.palette.background.overlay
      }
    }
  },
  conversationsContainer: {
    width: '100%',
    flex: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    bgcolor: darkMode ? 'transparent' : 'background.default',
    // No bottom padding needed
    pb: 0,
    // Ensure content is scrollable
    position: 'relative',
  },
  errorBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    p: 3,
    color: darkMode ? 'error.light' : 'error.main',
    bgcolor: alpha(theme.palette.error.main, darkMode ? 0.1 : 0.05),
    borderRadius: 1,
    '& .MuiTypography-root': {
      color: 'inherit'
    }
  },
  conversationGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    width: '100%',
    padding: '8px',
    borderRadius: 1,
    bgcolor: alpha(theme.palette.background.overlay, 0.5)
  },
  groupHeading: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    color: darkMode ? 'grey.300' : 'text.primary',
    transition: 'all 0.3s ease',
    px: 1
  },
  conversationCardsContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: '16px',
    width: '100%',
    justifyContent: 'flex-start'
  },
  emptyStateBox: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    '& .MuiCard-root': {
      minWidth: 275,
      bgcolor: darkMode ? theme.palette.background.glass : theme.palette.background.paper,
      color: darkMode ? 'grey.300' : 'text.primary',
      boxShadow: darkMode ? 'none' : 1,
      border: 1,
      borderColor: darkMode ? 'grey.700' : 'grey.200'
    }
  },
  emptyStateContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    p: 3,
    '& img': {
      width: '50%',
      marginBottom: 2,
      opacity: darkMode ? 0.8 : 1
    },
    '& .MuiTypography-root': {
      color: darkMode ? 'grey.400' : 'text.secondary',
      textAlign: 'center'
    },
    '& .MuiButton-root': {
      mt: 2,
      color: darkMode ? 'primary.light' : 'primary.main',
      borderColor: darkMode ? 'primary.light' : 'primary.main',
      '&:hover': {
        bgcolor: theme.palette.background.overlay
      }
    }
  },
  dayTypography: {
    m: 2,
    mt: 3,
    ml: 0,
    mb: 0,
    alignItems: 'center',
    color: darkMode ? 'grey.300' : 'text.primary',
    fontWeight: 500,
    fontSize: '0.875rem',
    textTransform: 'uppercase',
    letterSpacing: '0.1em'
  },
  dayCountTypography: {
    ml: 1,
    display: 'inline',
    fontWeight: 'normal',
    color: darkMode ? 'grey.400' : 'text.secondary',
    fontSize: '0.75rem'
  },

  conversationsEmptyContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    padding: 2
  },
  emptyContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    width: '100%',
    padding: 3,
    textAlign: 'center',
    minHeight: '60vh',
    position: 'relative'
  },
  cardContainer: {
    width: '100%',
    padding: '16px 24px',
    overflow: 'auto',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
    justifyContent: 'flex-start'
  },
  container: {
    // Add any necessary styles for the container
  },
  header: {
    // Add any necessary styles for the header
  },
  heading: {
    // Add any necessary styles for the heading
  },
  actions: {
    // Add any necessary styles for the actions
  },
  selectionActionsContainer: {
    // Add any necessary styles for the selection actions container
  },
  cardsContainer: {
    // Add any necessary styles for the cards container
  },
  noConversations: {
    // Add any necessary styles for the no conversations message
  }
});

type SortField = 'updated_at' | 'subject' | 'user_messages_count' | 'last_user_message_received_at' | 'latest_delivery_at' | 'channel_id';
type SortOrder = 'asc' | 'desc';

// @ts-ignore - Component returns React element
const ConversationsList: React.FC<ConversationsListProps> = ({ handleConversationsListToggleClick }) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const darkMode = theme.palette.mode === 'dark';
  const styles = getStyles(darkMode, theme);
  const [createConversationOpen, setCreateConversationOpen] = useState(false);
  const [mobileSelectMode, setMobileSelectMode] = useState(false);
  const [searchExpanded, setSearchExpanded] = useState(false); // Track if search is expanded on mobile
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const { loading: conversationsLoading, initialLoad: conversationsInitialLoad } = useAppSelector(state => state.conversations);
  const isFilterActive = useAppSelector(selectIsFilterActive);
  const unfilteredConversations = useAppSelector(selectConversationsWithContacts);
  const filteredConversations = useAppSelector(selectAllFilteredConversations);
  const channels = useAppSelector(state => state.channels?.channels || []);
  const priorityConversations = useAppSelector(selectPriorityConversations);
  
  // Add refs and state for intersection observer
  const observerRef = useRef<IntersectionObserver | null>(null);
  const [visibleConversationIds, setVisibleConversationIds] = useState<Set<string>>(new Set());
  const [loadingFullDataIds, setLoadingFullDataIds] = useState<Set<string>>(new Set());
  const visibleConversationsDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const [hasAttemptedInitialLoad, setHasAttemptedInitialLoad] = useState(false);
  const loadingFullDataIdsRef = useRef<Set<string>>(new Set());

  // Add search state
  const [searchTerm, setSearchTerm] = useState('');
  const searchDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [hasPendingSearch, setHasPendingSearch] = useState(false);
  const searchRequestRef = useRef<any>(null);

  // Add tags filter state
  const [selectedTagsFilter, setSelectedTagsFilter] = useState<string[]>([]);

  // Modify the conversations useMemo
  const conversations = useMemo(() => {
    const baseConversations = isFilterActive ? filteredConversations : unfilteredConversations;
    
    // Apply local filtering for any search term
    if (searchTerm && baseConversations.length > 0) {
      const searchLower = searchTerm.toLowerCase().trim();
      
      // If we're making an API call (searching for 3+ chars), still show local results immediately
      const filtered = baseConversations.filter(conv => {
        // Search in subject
        if (conv.subject && conv.subject.toLowerCase().includes(searchLower)) return true;
        
        // Search in participating entities (contacts)
        if (conv.participating_entities && conv.participating_entities.length > 0) {
          const hasMatchingContact = conv.participating_entities.some(entity => {
            // Check name and identifier (since Entity type has these)
            if (entity.name && entity.name.toLowerCase().includes(searchLower)) return true;
            if (entity.identifier && entity.identifier.toLowerCase().includes(searchLower)) return true;
            return false;
          });
          if (hasMatchingContact) return true;
        }
        
        // Search in last message content (if available)
        if (conv.messages && conv.messages.length > 0 && conv.messages[0].content) {
          if (conv.messages[0].content.toLowerCase().includes(searchLower)) return true;
        }
        
        return false;
      });
      return filtered;
    }
    
    return baseConversations;
  }, [isFilterActive, filteredConversations, unfilteredConversations, searchTerm]);

  // View mode removed - always using list view
  // Add state for sorting
  const [sortField] = useState<SortField>('updated_at');
  const [sortOrder] = useState<SortOrder>('desc');
  // Add state for selection
  const [selectedConversations, setSelectedConversations] = useState<string[]>([]);
  const [lastSelectedId, setLastSelectedId] = useState<string | null>(null);
  const [isDelivering, setIsDelivering] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isPausingResuming, setIsPausingResuming] = useState(false);
  const [totalToDeliver, setTotalToDeliver] = useState(0);
  const [totalToPauseResume, setTotalToPauseResume] = useState(0);
  const [deliveredCount, setDeliveredCount] = useState(0);
  const [pausedResumedCount, setPausedResumedCount] = useState(0);
  const { enqueueSnackbar } = useSnackbar();
  const [showGenerateRepliesDialog, setShowGenerateRepliesDialog] = useState(false);
  const [activeFilter, setActiveFilter] = useState<string>('any');
  // Always show selection column in list view
  const showSelectionColumn = true;
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const filterObject = useAppSelector((state) => state.filteredConversations.filterObject);
  const limit = useAppSelector(selectLimit);
  const hasMore = useAppSelector(selectHasMore);
  const hasMorePriority = useAppSelector(selectHasMorePriority);
  const lastCursor = useAppSelector(selectLastCursor);
  const filteredLastCursor = useAppSelector(selectFilteredLastCursor);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Get all conversations with pending assistant messages
  const conversationsWithPendingAssistant = useMemo(() => {
    return conversations.filter(conversation =>
      conversation.messages?.some(message =>
        message.status === 'pending_delivery' &&
        message.sender_role === 'assistant'
      )
    );
  }, [conversations]);


  // Handle selecting a conversation with spreadsheet-like behavior
  const handleSelectConversation = useCallback((conversationId: string, event?: React.MouseEvent, displayedConversations?: any[]) => {
    // IMPORTANT: Always use the displayed conversations array that's passed in
    if (!displayedConversations) {
      return;
    }
    
    const currentIndex = displayedConversations.findIndex(conv => conv.id === conversationId);
    
    if (currentIndex === -1) {
      return;
    }
    
    if (event && event.shiftKey && lastSelectedId) {
      // Find the index of the last selected conversation in the current displayed array
      const lastIndex = displayedConversations.findIndex(conv => conv.id === lastSelectedId);
      
      if (lastIndex !== -1) {
        // Shift+Click: Range selection
        const startIndex = Math.min(lastIndex, currentIndex);
        const endIndex = Math.max(lastIndex, currentIndex);
        const rangeIds = displayedConversations.slice(startIndex, endIndex + 1).map(conv => conv.id);
        
        // Replace selection with the range (clear previous and select range)
        setSelectedConversations(rangeIds);
        // Don't update lastSelectedId for shift+click to maintain the anchor point
      } else {
        // Last selected item is no longer in view, just select the current one
        setSelectedConversations([conversationId]);
        setLastSelectedId(conversationId);
      }
    } else if (event && (event.ctrlKey || event.metaKey)) {
      // Ctrl+Click (or Cmd+Click on Mac): Toggle individual selection
      if (selectedConversations.includes(conversationId)) {
        setSelectedConversations(prev => prev.filter(id => id !== conversationId));
        // If we're deselecting the last selected, clear it
        if (lastSelectedId === conversationId) {
          // Find another selected item to be the new anchor
          const remaining = selectedConversations.filter(id => id !== conversationId);
          setLastSelectedId(remaining.length > 0 ? remaining[remaining.length - 1] : null);
        }
      } else {
        setSelectedConversations(prev => [...prev, conversationId]);
        setLastSelectedId(conversationId);
      }
    } else {
      // Normal click: Single selection (clear others)
      if (selectedConversations.includes(conversationId) && selectedConversations.length === 1) {
        // If it's the only selected item, deselect it
        setSelectedConversations([]);
        setLastSelectedId(null);
      } else {
        // Otherwise, select only this item
        setSelectedConversations([conversationId]);
        setLastSelectedId(conversationId);
      }
    }
    
    // Enable mobile select mode when a conversation is selected on mobile
    const isMobile = window.innerWidth < theme.breakpoints.values.sm;
    if (isMobile && !mobileSelectMode) {
      setMobileSelectMode(true);
    }
  }, [lastSelectedId, selectedConversations, theme.breakpoints.values.sm, mobileSelectMode]);

  // Handle bulk delivery of selected messages
  const handleDeliverSelected = async () => {
    if (selectedConversations.length === 0) return;

    setIsDelivering(true);
    setIsGenerating(false);
    let pendingMessages: { messageId: string, channelId: string }[] = [];

    // Collect all pending assistant messages from selected conversations
    selectedConversations.forEach(conversationId => {
      const conversation = conversations.find(conv => conv.id === conversationId);
      if (conversation) {
        conversation.messages.forEach(message => {
          if (message.status === 'pending_delivery' && message.sender_role === 'assistant') {
            pendingMessages.push({
              messageId: message.id,
              channelId: conversation.channel_id
            });
          }
        });
      }
    });

    const totalMessages = pendingMessages.length;
    setTotalToDeliver(totalMessages);
    setDeliveredCount(0);

    // Track successful deliveries in a local variable
    let successCount = 0;

    // Process messages one by one
    for (let i = 0; i < pendingMessages.length; i++) {
      try {
        await dispatch(deliverMessageNow({
          messageId: pendingMessages[i].messageId,
          channelId: pendingMessages[i].channelId
        }));
        successCount++;
        setDeliveredCount(successCount);
      } catch (error) {
        console.error('Error delivering message:', error);
        enqueueSnackbar(`Failed to deliver message: ${error.message}`, {
          variant: 'error'
        });
      }
    }

    enqueueSnackbar(`Delivered ${successCount} of ${totalMessages} messages`, {
      variant: successCount === totalMessages ? 'success' : 'warning'
    });

    setIsDelivering(false);
    setSelectedConversations([]);

    // Refresh conversations
    dispatch(fetchConversationsWithMessages({}));
  };

  // Handle generating replies for multiple selected conversations
  const handleGenerateRepliesForSelected = () => {
    if (selectedConversations.length === 0) return;
    // Show the confirmation dialog instead of processing immediately
    setShowGenerateRepliesDialog(true);
  };

  // This will be called after the user confirms in the dialog
  const processGenerateRepliesForSelected = async () => {
    if (selectedConversations.length === 0) return;

    // Set a loading state
    setIsGenerating(true);
    setIsDelivering(false);

    const totalConversations = selectedConversations.length;
    setTotalToDeliver(totalConversations);
    setDeliveredCount(0);

    // Track successful generations in a local variable
    let successCount = 0;

    // Process conversations one by one
    for (let i = 0; i < selectedConversations.length; i++) {
      const conversationId = selectedConversations[i];
      const conversation = conversations.find(conv => conv.id === conversationId);

      if (conversation) {
        try {
          await dispatch(conversationAutocompleteNow({
            conversationId: conversation.id,
            channelId: conversation.channel_id
          }));
          successCount++;
          setDeliveredCount(successCount);
        } catch (error) {
          console.error('Error generating reply:', error);
          enqueueSnackbar(`Failed to generate reply for conversation: ${error.message}`, {
            variant: 'error'
          });
        }
      }
    }

    enqueueSnackbar(`Requested replies for ${successCount} of ${totalConversations} conversations`, {
      variant: successCount === totalConversations ? 'success' : 'warning'
    });

    setIsGenerating(false);
    // Keep conversations selected so user can see which ones got replies

    // Refresh conversations after a short delay to see the updated statuses
    setTimeout(() => {
      dispatch(fetchConversationsWithMessages({}));
    }, 1000);
  };

  // Handle posting function messages to all selected conversations
  const processFunctionMessageForSelected = async (functionMessageText: string, mediaUrls: string[] = []) => {
    if (selectedConversations.length === 0) return;

    // Set a loading state
    setIsGenerating(true);
    setIsDelivering(false);
    setTotalToDeliver(selectedConversations.length);
    setDeliveredCount(0);

    // Process conversations one by one
    for (let i = 0; i < selectedConversations.length; i++) {
      const conversationId = selectedConversations[i];
      const conversation = conversations.find(conv => conv.id === conversationId);

      if (conversation) {
        try {
          // Send a function message to the conversation using the API directly
          // rather than using the existing sendMessage action
          const url = `${process.env.REACT_APP_API_V3_URL}/chat/private/messages/post_message`;
          const data = {
            conversation_id: conversation.id,
            content: functionMessageText,
            from_role: 'function',
            channel_id: conversation.channel_id,
            media_urls: mediaUrls.length > 0 ? mediaUrls : undefined
          };

          await httpClient.post(url, data);

          // After posting the function message, trigger the autocomplete
          await dispatch(conversationAutocompleteNow({
            conversationId: conversation.id,
            channelId: conversation.channel_id
          }));

          setDeliveredCount(prev => prev + 1);
        } catch (error) {
          console.error('Error posting function message:', error);
          enqueueSnackbar(`Failed to post function message to conversation: ${error.message}`, {
            variant: 'error'
          });
        }
      }
    }

    enqueueSnackbar(`Posted function message to ${deliveredCount} of ${totalToDeliver} conversations`, {
      variant: deliveredCount === totalToDeliver ? 'success' : 'warning'
    });

    setIsGenerating(false);
    // Keep conversations selected so user can see which ones got messages

    // Refresh conversations after a short delay to see the updated statuses
    setTimeout(() => {
      dispatch(fetchConversationsWithMessages({}));
    }, 1000);
  };

  // Handle bulk update of delivery states for selected conversations
  const handleUpdateDeliveryStateForSelected = async (newState: 'play' | 'pause') => {
    if (selectedConversations.length === 0) return;

    // Set a loading state
    setIsPausingResuming(true);
    setIsDelivering(false);
    setIsGenerating(false);

    const totalConversations = selectedConversations.length;
    setTotalToPauseResume(totalConversations);
    setPausedResumedCount(0);

    // Track successful updates in a local variable
    let successCount = 0;

    // Process conversations one by one
    for (let i = 0; i < selectedConversations.length; i++) {
      const conversationId = selectedConversations[i];
      const conversation = conversations.find(conv => conv.id === conversationId);

      if (conversation) {
        try {
          await dispatch(updateConversationDeliveryState({
            conversationId: conversation.id,
            deliveryState: newState,
            channelId: conversation.channel_id
          }));
          successCount++;
          setPausedResumedCount(successCount);
        } catch (error) {
          console.error(`Error updating delivery state to ${newState}:`, error);
          enqueueSnackbar(`Failed to update delivery state: ${error.message}`, {
            variant: 'error'
          });
        }
      }
    }

    enqueueSnackbar(`Updated delivery state to ${newState} for ${successCount} of ${totalConversations} conversations`, {
      variant: successCount === totalConversations ? 'success' : 'warning'
    });

    setIsPausingResuming(false);
    // Keep conversations selected so user can see which ones were updated

    // Refresh conversations after a short delay to see the updated statuses
    setTimeout(() => {
      dispatch(fetchConversationsWithMessages({}));
    }, 1000);
  };

  const handleBulkDelete = async () => {
    if (selectedConversations.length === 0) {
      enqueueSnackbar('No conversations selected', { variant: 'warning' });
      return;
    }
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      // Using unwrap() properly types the result - this is preferable to using type assertions
      const result = await dispatch(deleteConversations(selectedConversations)).unwrap();

      if (result.successfulDeletes.length > 0) {
        enqueueSnackbar(`Successfully deleted ${result.successfulDeletes.length} conversations`, { variant: 'success' });
      }

      if (result.failedDeletes.length > 0) {
        enqueueSnackbar(`Failed to delete ${result.failedDeletes.length} conversations`, { variant: 'error' });
      }

      setSelectedConversations([]); // Clear selection after deletion
    } catch (error) {
      enqueueSnackbar('Error deleting conversations', { variant: 'error' });
    }
    setDeleteDialogOpen(false);
  };

  // Removed focusedConversation - handled directly in ConversationListView

  // Update the useEffect hook to not automatically select the first conversation
  useEffect(() => {
    // Don't automatically select the first conversation when the list loads
    // Also don't open the dialog if we're currently searching, have a pending search, or filters are active
    if (conversations.length === 0 && !conversationsLoading && conversationsInitialLoad && !isSearching && !searchTerm && !hasPendingSearch && !isFilterActive) {
      setCreateConversationOpen(true);
    }
  }, [conversations, conversationsLoading, conversationsInitialLoad, isSearching, searchTerm, hasPendingSearch, isFilterActive]);


  // Filter conversations based on the active filter
  const filteredAndSortedConversations = useMemo(() => {
    // If we have a search term, use the filtered conversations (already filtered locally)
    if (searchTerm) {
      console.log('Using search-filtered conversations:', conversations.length);
      return conversations;
    }
    
    // Filter based on active filter
    if (activeFilter === 'priority') {
      return priorityConversations;
    }
    
    return filterConversations(conversations, activeFilter);
  }, [conversations, activeFilter, sortField, sortOrder, searchTerm, priorityConversations]);

  // Also update handleFilterChange to clear search when switching to 'any' filter
  const handleFilterChange = (filterId: string) => {
    setActiveFilter(filterId);
    
    // Clear conversation_id and channel_id from URL when changing filters
    clearUrlParameters();
    
    if (filterId === 'priority') {
      // Fetch priority conversations when priority filter is selected
      dispatch(fetchPriorityConversations());
    } else if (filterId === 'any') {
      setSearchTerm('');
    }
  };

  // Helper function to clear conversation_id and channel_id from URL
  const clearUrlParameters = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const hasConversationId = urlParams.has('conversation_id');
    const hasChannelId = urlParams.has('channel_id');
    
    if (hasConversationId || hasChannelId) {
      urlParams.delete('conversation_id');
      urlParams.delete('channel_id');
      
      const newUrl = urlParams.toString() 
        ? `${window.location.pathname}?${urlParams.toString()}` 
        : window.location.pathname;
      
      window.history.pushState({}, '', newUrl);
    }
  };

  // Handle advanced filter change
  const handleAdvancedFilterChange = (advancedFilters: Record<string, any>) => {
    // Clear conversation_id and channel_id from URL when applying filters
    clearUrlParameters();
    
    // Apply the advanced filters using Redux
    dispatch(setFilter(advancedFilters));
    dispatch(fetchFilteredConversations(advancedFilters));
  };

  // Handle clearing all filters
  const handleClearAllFilters = () => {
    setActiveFilter('any');
    setSearchTerm('');
    
    const defaultLimit = 25;
    const defaultMessagesLimit = 3;
    
    // Dispatch to Redux with default limit values
    dispatch(setFilter({ limit: defaultLimit, messagesLimit: defaultMessagesLimit }));
    dispatch(fetchPriorityConversations()); // Re-fetch priority conversations to initial data
    
    // Show success notification
    enqueueSnackbar('Filters cleared successfully!', { variant: 'success' });
  };

  // Handle search input change
  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
    
    // Clear conversation_id and channel_id from URL when searching
    clearUrlParameters();
    
    // Cancel any existing search request
    if (searchRequestRef.current) {
      searchRequestRef.current.abort();
      searchRequestRef.current = null;
    }

    // Clear existing debounce timer
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }

    // If clearing the search, apply immediately without debounce
    if (!value.trim()) {
      setIsSearching(false);
      setHasPendingSearch(false);
      // Reset to default filters immediately, but respect the current active filter
      if (activeFilter !== 'any') {
        // If a specific filter is active, just remove the search but keep the filter
        dispatch(setFilter({ status: activeFilter, limit, messagesLimit: 3 }));
        dispatch(fetchFilteredConversations({ status: activeFilter, limit, messagesLimit: 3 }));
      } else {
        // If no specific filter, clear everything
        dispatch(setFilter({}));
        dispatch(fetchFilteredConversations({ limit, messagesLimit: 3 }));
      }
      return;
    }

    // For any non-empty search, we'll use local filtering immediately
    // and then make an API call for more comprehensive results
    if (value.length >= 3) {
      // Show that we have a pending search
      setHasPendingSearch(true);
      // Show searching state for longer searches that will hit the API
      setIsSearching(true);
    } else if (value.length > 0) {
      // For shorter searches, we're still "searching" locally
      setIsSearching(false); // Don't show loading for local-only searches
      setHasPendingSearch(false);
    }

    // Only make API call for searches >= 3 characters
    if (value.length >= 3) {
      // Debounce the actual search
      searchDebounceRef.current = setTimeout(() => {
        setHasPendingSearch(false); // Search is no longer pending
        const searchFilters = {
          universal_search: value.trim(),
          limit: Math.min(limit, 25), // Limit initial search results for performance
          messagesLimit: 0,
          full_payload: false,
          status: 'any',
        };
        
        // Apply the search filters
        dispatch(setFilter(searchFilters));
        
        // Store the request so we can cancel it if needed
        const request = dispatch(fetchFilteredConversations(searchFilters));
        searchRequestRef.current = request;
        
        request
          .unwrap()
          .then(() => {
            // Only update if this is still the current request
            if (searchRequestRef.current === request) {
              setIsSearching(false);
            }
          })
          .catch((error) => {
            // Check if it was aborted
            if (error.name !== 'AbortError' && searchRequestRef.current === request) {
              console.error('Search error:', error);
              setIsSearching(false);
            }
          });
      }, 150); // Reduced to 150ms for more responsive search
    }
  }, [dispatch, limit, activeFilter]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
      if (searchRequestRef.current) {
        searchRequestRef.current.abort();
      }
    };
  }, []);


  // Don't clear search automatically - let user control it
  // This was causing the search to reset after each keystroke

  const loadingSkeletons = Array.from(new Array(6)).map((_, index) => {
    return (
      <TableRow 
        key={index}
        sx={{
          backgroundColor: 'transparent',
          borderRadius: '8px',
          overflow: 'hidden',
          mx: 0,
          my: 0.5,
          boxShadow: `0 1px 3px ${alpha(theme.palette.primary.main, 0.05)}`,
          border: 'none',
          borderBottom: 'none !important',
          '& td': {
            border: 'none',
            borderBottom: 'none !important',
          }
        }}
      >
        {/* Checkbox skeleton */}
        {showSelectionColumn && (
          <TableCell padding="checkbox" sx={{ p: 0.0 }}>
            <Skeleton 
              variant="circular" 
              width={20} 
              height={20} 
            />
          </TableCell>
        )}
        
        {/* Status cell skeleton */}
        <TableCell sx={{ width: '120px', p: 0.75 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Skeleton 
              variant="circular" 
              width={20} 
              height={20} 
            />
            <Skeleton 
              variant="rounded" 
              width={80} 
              height={28} 
              sx={{ borderRadius: '4px' }} 
            />
          </Box>
        </TableCell>

        {/* Contact cell skeleton */}
        <TableCell sx={{ width: '180px', p: 0.75 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Skeleton 
              variant="circular" 
              width={40} 
              height={40} 
            />
            <Box>
              <Skeleton variant="text" width={100} height={18} />
              <Skeleton variant="text" width={120} height={14} />
            </Box>
          </Box>
        </TableCell>

        {/* Channel cell skeleton */}
        <TableCell sx={{ width: '200px', p: 0.75 }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1.5,
            p: 1,
            borderRadius: '4px'
          }}>
            <Skeleton 
              variant="circular" 
              width={40} 
              height={40} 
            />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width={100} height={18} />
              <Skeleton variant="text" width={140} height={14} />
            </Box>
          </Box>
        </TableCell>

        {/* Subject cell skeleton */}
        <TableCell sx={{ width: '220px', p: 0.75 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Skeleton variant="text" width="90%" height={18} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Skeleton variant="circular" width={12} height={12} />
              <Skeleton variant="text" width="60%" height={14} />
            </Box>
          </Box>
        </TableCell>

        {/* Message preview skeleton */}
        <TableCell sx={{ p: 0.75, minWidth: '250px' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <Skeleton 
              variant="circular" 
              width={32} 
              height={32} 
            />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width="85%" height={16} />
              <Skeleton variant="text" width="70%" height={14} />
              <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                <Skeleton 
                  variant="rounded" 
                  width={60} 
                  height={20} 
                  sx={{ borderRadius: '4px' }} 
                />
                <Skeleton 
                  variant="rounded" 
                  width={50} 
                  height={20} 
                  sx={{ borderRadius: '4px' }} 
                />
              </Box>
            </Box>
          </Box>
        </TableCell>

        {/* User messages count skeleton */}
        <TableCell align="center" sx={{ width: '120px', p: 0.75 }}>
          <Skeleton 
            variant="text" 
            width={40} 
            height={20} 
            sx={{ mx: 'auto' }} 
          />
        </TableCell>

        {/* Last user message skeleton */}
        <TableCell align="right" sx={{ width: '160px', p: 0.75 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
            <Skeleton variant="text" width={100} height={14} />
          </Box>
        </TableCell>

        {/* Updated at skeleton */}
        <TableCell align="right" sx={{ width: '140px', p: 0.75 }}>
          <Skeleton 
            variant="text" 
            width={110} 
            height={20} 
            sx={{ ml: 'auto' }} 
          />
        </TableCell>
      </TableRow>
    );
  });

  // Reset when filters change
  useEffect(() => {
    setHasAttemptedInitialLoad(false);
  }, [filterObject, limit, activeFilter]);

  // const showingMessage = () => {
  //   if (activeFilter === 'priority') {
  //     return `Showing ${filteredAndSortedConversations.length} priority conversations. ${hasMorePriority ? 'You can still load more in this category.' : 'No more conversations to load in this category.'}`;
  //   }
  //   return `Showing ${filteredAndSortedConversations.length} ${activeFilter.replace('_', ' ')} conversations. ${hasMore ? 'You can still load more in this category.' : 'No more conversations to load in this category.'}`;
  // }

  const selectedConversationsData = useMemo(() => {
    return conversations.filter(conv => selectedConversations.includes(conv.id));
  }, [conversations, selectedConversations]);

  const anyPausedSelected = selectedConversationsData.some(conv => conv.delivery_state === 'pause');
  const anyPlayingSelected = selectedConversationsData.some(conv => conv.delivery_state === 'play');

  // Keep selectedConversations in sync with available conversations (e.g. after deletes)
  useEffect(() => {
    setSelectedConversations(prev => prev.filter(id => conversations.some(conv => conv.id === id)));
  }, [conversations]);

  // Handle loading more conversations for infinite scroll
  const handleLoadMore = useCallback(async () => {
    // Use the appropriate cursor based on filter state
    const currentCursor = isFilterActive ? filteredLastCursor : lastCursor;
    const currentHasMore = isFilterActive ? hasMorePriority : hasMore;

    if (isLoadingMore || !currentHasMore || !currentCursor) return;

    setIsLoadingMore(true);

    try {
      // Prepare parameters based on current filters
      const params: any = {
        cursor: currentCursor,
        full_payload: false,
        limit: 25,
        messagesLimit: 0
      };

      // Add tags filter if selected
      if (selectedTagsFilter.length > 0) {
        params.tags = selectedTagsFilter;
      }

      // If filters are active, include them
      if (isFilterActive && filterObject) {
        Object.assign(params, filterObject);
      }

      // Use the appropriate fetch based on whether filters are active
      if (isFilterActive) {
        await dispatch(fetchFilteredConversations(params));
      } else {
        await dispatch(fetchConversationsWithMessages(params));
      }
    } catch (error) {
      console.error('Error loading more conversations:', error);
      enqueueSnackbar('Failed to load more conversations', { variant: 'error' });
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, hasMore, hasMorePriority, lastCursor, filteredLastCursor, isFilterActive, filterObject, selectedTagsFilter, dispatch, enqueueSnackbar]);

  // Set up intersection observer to track visible conversations
  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        // Clear any existing debounce timer
        if (visibleConversationsDebounceRef.current) {
          clearTimeout(visibleConversationsDebounceRef.current);
        }

        // Debounce the visible conversations update
        visibleConversationsDebounceRef.current = setTimeout(() => {
          setVisibleConversationIds((prev) => {
            const newSet = new Set(prev);
            entries.forEach((entry) => {
              const conversationId = entry.target.getAttribute('data-conversation-id');
              if (conversationId) {
                if (entry.isIntersecting) {
                  newSet.add(conversationId);
                } else {
                  newSet.delete(conversationId);
                }
              }
            });
            return newSet;
          });
        }, 100); // 100ms debounce
      },
      {
        rootMargin: '100px', // Load data for conversations 100px before they come into view
        threshold: 0.01,
      }
    );

    return () => {
      observerRef.current?.disconnect();
      if (visibleConversationsDebounceRef.current) {
        clearTimeout(visibleConversationsDebounceRef.current);
      }
    };
  }, []);

  // Load full data for visible conversations that don't have it yet
  useEffect(() => {
    if (conversationsLoading) return;
    if (conversationsInitialLoad) return; // Don't fetch during initial load
    
    // Only check visible conversations, not all displayed ones
    const conversationsToCheck = Array.from(visibleConversationIds)
      .map(id => filteredAndSortedConversations.find(c => c.id === id))
      .filter(Boolean);

    const conversationsNeedingFullData = conversationsToCheck
      .filter(conversation => {
        // Skip if already has full data
        if (conversation._hasFullData === true) return false;
        // Skip if already loading
        if (loadingFullDataIdsRef.current.has(conversation.id)) return false;
        // Skip if already has messages (more than the initial 3)
        if (conversation.messages && conversation.messages.length > 3) return false;
        return true;
      })
      .map(conversation => conversation.id);

    if (conversationsNeedingFullData.length > 0) {
      // Mark these conversations as loading
      conversationsNeedingFullData.forEach(id => loadingFullDataIdsRef.current.add(id));
      setLoadingFullDataIds(prev => new Set([...prev, ...conversationsNeedingFullData]));

      // Batch requests to avoid overwhelming the backend (max 10 per request for better control)
      const batchSize = 10;
      const batches = [];
      for (let i = 0; i < conversationsNeedingFullData.length; i += batchSize) {
        batches.push(conversationsNeedingFullData.slice(i, i + batchSize));
      }

      // Process batches sequentially to avoid rate limiting
      const processBatches = async () => {
        for (const batch of batches) {
          try {
            await dispatch(fetchConversationsFullData(batch)).unwrap();
            // Add a small delay between batches to prevent overwhelming the server
            if (batches.length > 1) {
              await new Promise(resolve => setTimeout(resolve, 100));
            }
          } catch (error) {
            console.error('Error fetching full data for batch:', error);
            // Remove failed IDs from loading set
            batch.forEach((id: string) => loadingFullDataIdsRef.current.delete(id));
          }
        }
      };

      processBatches().finally(() => {
        // Remove from loading set
        conversationsNeedingFullData.forEach((id: string) => loadingFullDataIdsRef.current.delete(id));
        setLoadingFullDataIds(prev => {
          const newSet = new Set(prev);
          conversationsNeedingFullData.forEach((id: string) => newSet.delete(id));
          return newSet;
        });
      });
    }
  }, [visibleConversationIds, conversationsLoading, conversationsInitialLoad, dispatch]); // Removed filteredAndSortedConversations dependency

  // Mark initial load as attempted once conversations are loaded
  useEffect(() => {
    if (!conversationsLoading && conversations.length > 0 && !hasAttemptedInitialLoad) {
      setHasAttemptedInitialLoad(true);
    }
  }, [conversationsLoading, conversations.length, hasAttemptedInitialLoad]);

  return (
    <Box className="conversations-list" sx={{ 
      ...styles.mainContainer,
      display: 'flex',
      flexDirection: 'column',
      position: 'relative'
    }}>
      {/* Removed view mode toggle - always use list view */}

      {/* Search and Filters Bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: { xs: 0, sm: 2 },
          py: { xs: 1.5, sm: 1 }, // More padding on mobile
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: darkMode ? 'background.paper' : 'background.default',
          position: 'relative',
          overflow: 'hidden',
          minHeight: { xs: 64, sm: 56 }, // Taller on mobile
          flexShrink: 0, // Prevent shrinking
        }}
      >
        {/* Search Input - Mobile optimized */}
        <Box ref={searchContainerRef} sx={{ flexShrink: 0 }}>
          <Tooltip title="Search by subject, contact name, or entity identifier" arrow placement="bottom">
            <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: { xs: searchExpanded || searchTerm ? 1.5 : 1, sm: 1.5 },
                  py: 0.5,
                  borderRadius: '20px', // More rounded on mobile
                  bgcolor: darkMode ? alpha(theme.palette.grey[700], 0.8) : alpha(theme.palette.grey[200], 0.8),
                  border: `1px solid ${darkMode ? theme.palette.grey[600] : theme.palette.grey[300]}`,
                  minWidth: { xs: searchExpanded || searchTerm ? '140px' : '40px', sm: '250px' },
                  maxWidth: { xs: searchExpanded || searchTerm ? '180px' : '40px', sm: '400px' },
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    bgcolor: darkMode ? theme.palette.grey[700] : theme.palette.grey[300],
                  },
                  '&:focus-within': {
                    borderColor: theme.palette.primary.main,
                  }
                }}
                onClick={() => {
                  // On mobile, clicking the container expands it
                  if (window.innerWidth < theme.breakpoints.values.sm && !searchExpanded) {
                    setSearchExpanded(true);
                    setTimeout(() => searchInputRef.current?.focus(), 100);
                  }
                }}
              >
                <SearchIcon fontSize="small" sx={{ color: darkMode ? 'grey.400' : 'grey.600' }} />
                <InputBase
                  ref={searchInputRef}
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  autoComplete="off"
                  onBlur={() => {
                    // Collapse on mobile when empty and loses focus
                    if (window.innerWidth < theme.breakpoints.values.sm && !searchTerm) {
                      setSearchExpanded(false);
                    }
                  }}
                  sx={{
                    ml: 0.5,
                    flex: 1,
                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                    fontWeight: 400,
                    color: darkMode ? 'grey.100' : 'text.primary',
                    display: { xs: searchExpanded || searchTerm ? 'block' : 'none', sm: 'block' },
                    '& input': {
                      padding: 0,
                    },
                    '& input::placeholder': {
                      opacity: 0.7,
                      color: darkMode ? 'grey.500' : 'text.secondary',
                    }
                  }}
                />
                {searchTerm && (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSearchChange('');
                    }}
                    sx={{ 
                      p: 0.25,
                      color: darkMode ? 'grey.400' : 'grey.600',
                      '&:hover': {
                        bgcolor: 'transparent',
                        color: darkMode ? 'grey.200' : 'grey.800',
                      }
                    }}
                  >
                    <ClearIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                )}
                {isSearching && (
                  <CircularProgress size={16} sx={{ ml: 0.5, color: theme.palette.primary.main }} />
                )}
              </Box>
            </Box>
          </Tooltip>
        </Box>

        {/* ConversationFilters - with horizontal scroll */}
        <Box sx={{ 
          flex: 1,
          overflow: 'hidden',
          position: 'relative',
        }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            overflowX: 'auto',
            overflowY: 'hidden',
            scrollbarWidth: 'none', // Firefox
            '&::-webkit-scrollbar': {
              display: 'none', // Chrome, Safari, Edge
            },
            // Add fade effect on edges for better UX
            '&::before': {
              content: '""',
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: '20px',
              background: `linear-gradient(to right, ${darkMode ? theme.palette.background.paper : theme.palette.background.default}, transparent)`,
              zIndex: 1,
              pointerEvents: 'none',
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              right: 0,
              top: 0,
              bottom: 0,
              width: '20px',
              background: `linear-gradient(to left, ${darkMode ? theme.palette.background.paper : theme.palette.background.default}, transparent)`,
              zIndex: 1,
              pointerEvents: 'none',
            }
          }}>
            <ConversationFilters
              selectedFilter={activeFilter}
              onFilterChange={handleFilterChange}
              conversations={conversations}
              onAdvancedFilterChange={handleAdvancedFilterChange}
              channels={channels || []}
            />
          </Box>
        </Box>
      </Box>


      {/* Pagination moved to sticky bottom bar */}

            {/* Unified Selection Bar - Moved to ConversationsTable component */}
      {selectedConversations.length > 0 && (
        <Box
          sx={{
            width: '100%',
            py: { xs: 0.75, sm: 1.25 },
            px: { xs: 0, sm: 2 },
            bgcolor: darkMode ? 'rgba(255, 255, 255, 0.1)' : alpha(theme.palette.primary.main, 0.08),
            color: theme.palette.text.primary,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid',
            borderColor: 'divider',
            position: 'relative',
            minHeight: { xs: 48, sm: 64 },
          }}
        >
          {/* Left side - selection info */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1.5 } }}>
            <IconButton
              size="small"
              onClick={() => {
                setSelectedConversations([]);
                setMobileSelectMode(false);
              }}
              sx={{ 
                color: theme.palette.text.primary,
                p: { xs: 0.5, sm: 0.75 },
                '&:hover': {
                  bgcolor: theme.palette.action.hover
                }
              }}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
            <Typography 
              variant="body1" 
              sx={{ 
                fontWeight: 600, 
                fontSize: { xs: '0.875rem', sm: '1.125rem' },
                color: theme.palette.text.primary,
              }}
            >
              {selectedConversations.length} selected
            </Typography>
          </Box>
          
          {/* Right side - actions */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.25, sm: 1 } }}>
            {/* Mobile: Only show essential actions */}
            <Box sx={{ display: { xs: 'flex', sm: 'none' }, gap: 0.25 }}>
              <IconButton
                onClick={handleBulkDelete}
                disabled={isPausingResuming || isDelivering || isGenerating}
                size="small"
                sx={{ 
                  color: theme.palette.text.primary,
                  p: 0.5,
                }}
              >
                <Delete fontSize="small" />
              </IconButton>
              
              <IconButton
                onClick={handleGenerateRepliesForSelected}
                disabled={isDelivering || isGenerating}
                size="small"
                sx={{ 
                  color: theme.palette.text.primary,
                  p: 0.5,
                  position: 'relative',
                }}
              >
                <AutoAwesome fontSize="small" />
                {isGenerating && (
                  <CircularProgress size={12} sx={{ position: 'absolute', color: theme.palette.text.primary }} />
                )}
              </IconButton>

              {conversationsWithPendingAssistant.some(conv => selectedConversations.includes(conv.id)) && (
                <IconButton
                  onClick={handleDeliverSelected}
                  disabled={isDelivering || isGenerating}
                  size="small"
                  sx={{ 
                    color: theme.palette.text.primary,
                    p: 0.5,
                    position: 'relative',
                  }}
                >
                  <Send fontSize="small" />
                  {isDelivering && (
                    <CircularProgress size={12} sx={{ position: 'absolute', color: theme.palette.text.primary }} />
                  )}
                </IconButton>
              )}
            </Box>

            {/* Desktop: Show all actions with tooltips */}
            <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 1 }}>
              <Tooltip title="Delete conversations" arrow>
                <IconButton
                  onClick={handleBulkDelete}
                  disabled={isPausingResuming || isDelivering || isGenerating}
                  sx={{ 
                    color: theme.palette.text.primary,
                    p: 1,
                    '&:hover': { 
                      bgcolor: alpha(theme.palette.error.main, 0.1),
                      color: theme.palette.error.main
                    },
                    '&.Mui-disabled': {
                      color: theme.palette.action.disabled
                    }
                  }}
                >
                  <Delete fontSize="medium" />
                </IconButton>
              </Tooltip>

              <Tooltip title="Generate AI replies" arrow>
                <IconButton
                  onClick={handleGenerateRepliesForSelected}
                  disabled={isDelivering || isGenerating}
                  sx={{ 
                    color: theme.palette.text.primary,
                    p: 1,
                    position: 'relative',
                    '&:hover': { 
                      bgcolor: alpha(theme.palette.secondary.main, 0.1),
                      color: theme.palette.secondary.main
                    },
                    '&.Mui-disabled': {
                      color: theme.palette.action.disabled
                    }
                  }}
                >
                  <AutoAwesome fontSize="medium" />
                  {isGenerating && (
                    <CircularProgress 
                      size={16} 
                      sx={{ 
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        marginTop: '-8px',
                        marginLeft: '-8px',
                        color: theme.palette.secondary.main
                      }} 
                    />
                  )}
                </IconButton>
              </Tooltip>

              {conversationsWithPendingAssistant.some(conv => selectedConversations.includes(conv.id)) && (
                <Tooltip title="Deliver pending messages" arrow>
                  <IconButton
                    onClick={handleDeliverSelected}
                    disabled={isDelivering || isGenerating}
                    sx={{ 
                      color: theme.palette.text.primary,
                      p: 1,
                      position: 'relative',
                      '&:hover': { 
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.main
                      },
                      '&.Mui-disabled': {
                        color: theme.palette.action.disabled
                      }
                    }}
                  >
                    <Send fontSize="medium" />
                    {isDelivering && (
                      <CircularProgress 
                        size={16} 
                        sx={{ 
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          marginTop: '-8px',
                          marginLeft: '-8px',
                          color: theme.palette.primary.main
                        }} 
                      />
                    )}
                  </IconButton>
                </Tooltip>
              )}

              {anyPausedSelected && (
                <Tooltip title="Enable auto-reply" arrow>
                  <IconButton
                    onClick={() => handleUpdateDeliveryStateForSelected('play')}
                    disabled={isPausingResuming || isDelivering || isGenerating}
                    sx={{ 
                      color: theme.palette.text.primary,
                      p: 1,
                      '&:hover': { 
                        bgcolor: alpha(theme.palette.success.main, 0.1),
                        color: theme.palette.success.main
                      },
                      '&.Mui-disabled': {
                        color: theme.palette.action.disabled
                      }
                    }}
                  >
                    <PlayArrow fontSize="medium" />
                  </IconButton>
                </Tooltip>
              )}

              {anyPlayingSelected && (
                <Tooltip title="Pause auto-reply" arrow>
                  <IconButton
                    onClick={() => handleUpdateDeliveryStateForSelected('pause')}
                    disabled={isPausingResuming || isDelivering || isGenerating}
                    sx={{ 
                      color: theme.palette.text.primary,
                      p: 1,
                      '&:hover': { 
                        bgcolor: alpha(theme.palette.warning.main, 0.1),
                        color: theme.palette.warning.main
                      },
                      '&.Mui-disabled': {
                        color: theme.palette.action.disabled
                      }
                    }}
                  >
                    <Pause fontSize="medium" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>

            {/* Progress indicators - simplified on mobile */}
            {(isDelivering || isGenerating || isPausingResuming) && (
              <Box sx={{ 
                display: { xs: 'none', sm: 'flex' }, 
                alignItems: 'center',
                ml: 1,
                px: 1.5,
                py: 0.5,
                borderRadius: 1,
                bgcolor: theme.palette.background.overlayLight
              }}>
                <Typography variant="caption" sx={{ fontWeight: 500 }}>
                  {isDelivering ? `${deliveredCount}/${totalToDeliver}` : 
                   isGenerating ? `${deliveredCount}/${totalToDeliver}` :
                   `${pausedResumedCount}/${totalToPauseResume}`}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}


      <Box sx={{ 
        flex: 1, 
        minHeight: 0,
        display: 'flex', 
        flexDirection: 'column',
        overflow: 'hidden',
        width: '100%',
        position: 'relative',
      }}>
        <Hidden mdDown>
          {/* Conversations view */}
          {conversationsInitialLoad && conversationsLoading && filteredAndSortedConversations.length === 0 ? (
            <Box sx={{
              flex: 1,
              overflow: 'auto',
              '&::-webkit-scrollbar': {
                width: '8px'
              },
              '&::-webkit-scrollbar-track': {
                bgcolor: darkMode ? 'grey.800' : 'grey.100'
              },
              '&::-webkit-scrollbar-thumb': {
                bgcolor: darkMode ? 'grey.700' : 'grey.300',
                borderRadius: '4px',
              }
            }}>
              <Table sx={{
                '& .MuiTableCell-root': {
                  border: 'none',
                  borderBottom: 'none !important',
                },
                '& .MuiTableRow-root': {
                  border: 'none',
                  borderBottom: 'none !important',
                }
              }}>
                <TableBody>
                  {loadingSkeletons}
                </TableBody>
              </Table>
            </Box>
          ) : filteredAndSortedConversations.length === 0 && !isLoadingMore ? (
            <Box sx={{...styles.emptyContainer, flex: 1}}>
              {isSearching ? (
                <>
                  <CircularProgress size={48} sx={{ mb: 3 }} />
                  <Typography variant="h6" color="textSecondary">
                    Searching conversations...
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                    Looking for "{searchTerm}"
                  </Typography>
                </>
              ) : (
                <>
                  <img src={emptyConversationsListSvg} alt="No conversations found" style={{ maxWidth: '200px', opacity: 0.8 }} />
                  <Typography variant="h6" color="textSecondary" sx={{ mt: 2 }}>
                    No conversations found
                  </Typography>
                </>
              )}
              {(isFilterActive || searchTerm) && !isSearching ? (
                <>
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 1, mb: 3, textAlign: 'center' }}>
                    No conversations match your current filters or search criteria. Try clearing your filters or adjusting your search.
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<FilterListOff />}
                    onClick={handleClearAllFilters}
                    sx={{ mr: 1 }}
                  >
                    Clear All Filters
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<AddComment />}
                    onClick={() => setCreateConversationOpen(true)}
                  >
                    New Conversation
                  </Button>
                </>
              ) : !isSearching ? (
                <>
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 1, mb: 3, textAlign: 'center' }}>
                    Start a new conversation by clicking the button below
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddComment />}
                    onClick={() => setCreateConversationOpen(true)}
                  >
                    New Conversation
                  </Button>
                </>
              ) : null}
            </Box>
          ) : (
            <Box
              sx={{
                flex: 1,
                width: '100%',
                maxWidth: '100%',
                overflowY: 'auto',
                overflowX: 'hidden',
                '&::-webkit-scrollbar': {
                  width: '8px'
                },
                '&::-webkit-scrollbar-track': {
                  bgcolor: 'transparent'
                },
                '&::-webkit-scrollbar-thumb': {
                  bgcolor: darkMode ? 'grey.700' : 'grey.300',
                  borderRadius: '4px',
                }
              }}>
              <ConversationsTable
                conversations={filteredAndSortedConversations}
                handleConversationsListToggleClick={handleConversationsListToggleClick}
                selectedConversations={selectedConversations}
                onSelectConversation={(id, event) => handleSelectConversation(id, event, filteredAndSortedConversations)}
                showSelectionColumn={showSelectionColumn}
                initialSortField={sortField}
                initialSortOrder={sortOrder}
                loadingFullDataIds={loadingFullDataIds}
                channels={channels}
                mobileSelectMode={mobileSelectMode}
                onLoadMore={handleLoadMore}
                hasMore={isFilterActive ? hasMorePriority : hasMore}
                isLoadingMore={isLoadingMore}
                selectedTagsFilter={selectedTagsFilter}
                onTagsFilterChange={setSelectedTagsFilter}
              />
              
            </Box>
          )}
        </Hidden>
        
        {/* Mobile view */}
        <Hidden mdUp>
          <Box
            sx={{
              flex: 1,
              width: '100%',
              maxWidth: '100%',
              overflowY: 'auto',
              overflowX: 'hidden',
              '&::-webkit-scrollbar': {
                width: '8px'
              },
              '&::-webkit-scrollbar-track': {
                bgcolor: 'transparent'
              },
              '&::-webkit-scrollbar-thumb': {
                bgcolor: darkMode ? 'grey.700' : 'grey.300',
                borderRadius: '4px',
              }
            }}>
            <ConversationsTable
              conversations={filteredAndSortedConversations}
              handleConversationsListToggleClick={handleConversationsListToggleClick}
              selectedConversations={selectedConversations}
              onSelectConversation={(id, event) => handleSelectConversation(id, event, filteredAndSortedConversations)}
              showSelectionColumn={showSelectionColumn}
              initialSortField={sortField}
              initialSortOrder={sortOrder}
              loadingFullDataIds={loadingFullDataIds}
              channels={channels}
              mobileSelectMode={mobileSelectMode}
              onLoadMore={handleLoadMore}
              hasMore={isFilterActive ? hasMorePriority : hasMore}
              isLoadingMore={isLoadingMore}
              selectedTagsFilter={selectedTagsFilter}
              onTagsFilterChange={setSelectedTagsFilter}
            />
          </Box>
        </Hidden>
      </Box>

      <ConversationCreateDialogV2
        open={createConversationOpen}
        handleClose={() => setCreateConversationOpen(false)}
      />

      <GenerateRepliesConfirmationDialog
        open={showGenerateRepliesDialog}
        onClose={() => setShowGenerateRepliesDialog(false)}
        onConfirm={(functionMessage?: string, fileUrls?: string[]) => {
          setShowGenerateRepliesDialog(false);
          if (functionMessage) {
            processFunctionMessageForSelected(functionMessage, fileUrls);
          } else {
            processGenerateRepliesForSelected();
          }
        }}
        selectedConversations={selectedConversations.map(id => conversations.find(conv => conv.id === id)).filter(Boolean)}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Confirm Bulk Delete
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete {selectedConversations.length} conversations? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
      );
  };

export default ConversationsList;
